import React, { createContext, useEffect, useReducer } from 'react';
import { useLocation } from 'react-router-dom';
import set from 'lodash/set';
import cloneDeep from 'lodash/cloneDeep';

const QuickSetupContext = createContext();
const initialState = {
  selectedValues: {
    validation: {
      variants: [],
      channels: []
    },
    aep: {
      variants: [],
      channels: []
    },
    dataImport: {
      variants: [],
      channels: []
    },
    sources: {
      variants: [],
      channels: []
    },
    offers: {
      variants: [],
      channels: []
    },
    exd: {
      variants: [],
      channels: []
    },
    ajo: {
      variants: [],
      channels: []
    },
    loyalty: {
      variants: [],
      channels: []
    },
    launch: {
      variants: [],
      channels: []
    },
    projects: {
      variants: [],
      channels: []
    }
  },
  jobs: [
    {
      name: 'initial-validation',
      title: 'Initial Validation',
      path: 'validation',
      params: {},
      pollingInterval: 2
    },
    {
      name: 'configure-aep',
      title: 'Core AEP',
      path: 'aep',
      params: {},
      pollingInterval: 7
    },
    {
      name: 'data-import',
      title: 'Data Import',
      path: 'dataImport',
      params: {},
      pollingInterval: 7
    },
    {
      name: 'configure-sources',
      title: 'Sources & Destinations',
      path: 'sources',
      params: {},
      pollingInterval: 7
    },
    {
      name: 'configure-ode',
      title: 'Offer Decisioning',
      path: 'offers',
      params: {},
      pollingInterval: 7
    },
    {
      name: 'configure-exd',
      title: 'Experience Decisioning',
      path: 'exd',
      params: {},
      pollingInterval: 7
    },
    {
      name: 'configure-ajo',
      title: 'Journey Optimizer',
      path: 'ajo',
      params: {},
      pollingInterval: 7
    },
    {
      name: 'configure-ajo-loyalty',
      title: 'AJO Loyalty',
      path: 'loyalty',
      params: {},
      pollingInterval: 7
    },
    {
      name: 'create-projects',
      title: 'Projects',
      path: 'projects',
      params: {},
      pollingInterval: 2
    },
    {
      name: 'configure-launch',
      title: 'Data Collection (Launch)',
      path: 'launch',
      params: {},
      pollingInterval: 7
    },
    {
      name: 'post-processing',
      title: 'Post Processing',
      path: 'postProcessing',
      params: {
        addIntegrationsToProjects: true
      },
      pollingInterval: 2
    }
  ],
  additionalParams: {},
  // When true (default), Quick Setup creates/links a Live Demos workspace after jobs finish.
  registerInLivedemos: true
};

const reducer = (state, action) => {
  const { path, value } = action;

  // set a value in state object under selected path
  let result = set({ ...state }, path, value);

  // when selecting a preset, make sure its values overwrite currently selected values
  // (value can be null/undefined, e.g. when Environment changes and the preset
  // selection is cleared - in that case selectedValues just resets to defaults)
  if (path === 'preset') {
    result = {
      ...result,
      selectedValues: {
        ...cloneDeep(initialState.selectedValues),
        ...cloneDeep(value?.values)
      }
    };

    // Update addIntegrationsToProjects based on attachDataCollection
    const shouldAddIntegrations = value ? value.attachDataCollection !== false : true;
    const postProcessingIndex = result.jobs.findIndex(
      (job) => job.name === 'post-processing'
    );
    if (postProcessingIndex !== -1) {
      const updatedJobs = [...result.jobs];
      updatedJobs[postProcessingIndex].params.addIntegrationsToProjects =
        shouldAddIntegrations;
      result = { ...result, jobs: updatedJobs };
    }
  }

  return result;
};

const QuickSetupContextProvider = (props) => {
  const { search } = useLocation();
  const workspaceParam = new URLSearchParams(search).get('workspace');

  const [context, setContextValue] = useReducer(
    reducer,
    cloneDeep(initialState)
  );

  useEffect(() => {
    if (workspaceParam) {
      setContextValue({ path: 'workspaceId', value: workspaceParam });
    }
  }, [workspaceParam]);

  return (
    <QuickSetupContext.Provider value={{ context, setContextValue }}>
      {props.children}
    </QuickSetupContext.Provider>
  );
};

export { QuickSetupContext, QuickSetupContextProvider };
